/* eslint-disable react/jsx-props-no-spreading */

import {isUndefined, noop} from 'lodash';
import {Duration} from 'luxon';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {boxedInputColorReaders} from '../../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputConstants';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights, secondaryFontSizes} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {PaletteColorsEnum} from '../../../../../../libs/shared/ui/src/styles/paletteModel';
import {UserTheme} from '../../../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {DDHHMM, delayToDayHHMM, DelayUnitsEnum} from '../../../../../settings/editors/commons/paramModel';
import {InputDDHHMM} from '../../../../../settings/rules/ruleEditor/inputs/inputDDHHMM';
import {getDonutArcIndexColor} from '../../../../helpers/analyticsFormatHelpers';
import {
  BucketsPickerProps,
  maybeRenderPickerErrorMessage,
  maybeRenderPickerMetricSubtitle,
  renderPickerButtons,
  StyledPickerInputRowDiv,
  StyledPickerRowColor,
} from './common';

/*
 * Style.
 */

const StyledPickerTimeInputDiv = styled.div<{$isDisabled?: boolean}>`
  width: 127px;
  margin: 10px 4px;
  opacity: ${(p) => (p.$isDisabled ? 0.5 : 1)};
`;

const DropdownInputHeaderWrapperDiv = styled.div`
  display: flex;
  vertical-align: middle;
  flex-direction: row;
  margin-left: 40px;
  margin-bottom: -4px;
`;

const DropdownInputHeaderDiv = styled.div`
  width: 127px;
  margin: 0px 4px;
  font-weight: ${fontWeights.semibold};
  color: ${boxedInputColorReaders.textLabel};
  font-size: ${(p) => secondaryFontSizes[VisualSizesEnum.MEDIUM]};
`;

/*
 * Constants.
 */

const errorMessage = (
  <Trans id="analytics-element-bucket-time-picker-error-state">
    Each bucket&apos;s end time must be after the start time.
  </Trans>
);

/*
 * Component.
 */

export const TimeBucketsPicker: FC<BucketsPickerProps> = ({
  metric,
  bucketBoundaries,
  color,
  onRequestClose,
  onApply,
}) => {
  const theme = useUserTheme();

  const metricId = metric.listHistoMetricId;

  const [bucketEndTimes, setBucketEndTimes] = useState<ReadonlyArray<DDHHMM>>(() => {
    // At least in the current iteration the picker only supports exactly
    // 4 end times, so if we get anything else just default to all 0's.
    const bucketBoundary = bucketBoundaries?.[metricId];
    if (isUndefined(bucketBoundary) || bucketBoundary.length !== 4) {
      return [{}, {}, {}, {}];
    }

    return bucketBoundary.map((boundary) => calculateDDHHMMForMs(boundary));
  });

  const [shouldShowErrorState, setShouldShowErrorState] = useState<boolean>(false);

  const onApplyClick = () => {
    const newValues = bucketEndTimes.map((endtime) => calculateMsForBucket(endtime));
    if (!computeAreBucketsValid(newValues)) {
      setShouldShowErrorState(true);
      return;
    }

    onApply(metricId, {
      [metricId]: newValues,
    });
  };

  const setBucketEndtime = (index: number, value: DDHHMM) => {
    const newBucketEndTimes = [...bucketEndTimes];
    newBucketEndTimes[index] = value;
    setBucketEndTimes(sanitizeBuckets(newBucketEndTimes, index));
  };

  return (
    <>
      <DropdownInputHeaderWrapperDiv>
        <DropdownInputHeaderDiv>
          <Trans id="analytics-time-buckets-input-start">From</Trans>
        </DropdownInputHeaderDiv>
        <DropdownInputHeaderDiv>
          <Trans id="analytics-time-buckets-input-end">To</Trans>
        </DropdownInputHeaderDiv>
      </DropdownInputHeaderWrapperDiv>

      {bucketEndTimes.map((endTime, index) => {
        // startTime is undefined for the first bucket since it should always
        // just start at 0, otherwise is the endtime of the previous bucket.
        const startTime = index === 0 ? undefined : bucketEndTimes[index - 1];
        return renderTimeBucketEntryRow({
          index,
          startTime,
          endTime,
          setEndTime: (value: DDHHMM) => setBucketEndtime(index, value),
          error: shouldShowErrorState && computeShouldShowErrorStateForBucket(endTime, startTime),
          color,
          theme,
        });
      })}
      {/* Last bucket row, doesn't have any end time entry. */}
      {renderTimeBucketEntryRow({index: 4, startTime: bucketEndTimes[3], color, theme})}
      {maybeRenderPickerMetricSubtitle(metric.metricBucketPickerSubtitle)}
      {maybeRenderPickerErrorMessage(shouldShowErrorState, errorMessage)}
      {renderPickerButtons(onRequestClose, onApplyClick)}
    </>
  );
};

/*
 * Helpers.
 */

const renderTimeBucketEntryRow = ({
  index,
  startTime,
  endTime,
  setEndTime,
  error,
  color,
  theme,
}: {
  index: number;
  startTime?: DDHHMM;
  endTime?: DDHHMM;
  setEndTime?: HandlerOf<DDHHMM>;
  error?: boolean;
  color: PaletteColorsEnum;
  theme: UserTheme;
}) => (
  <StyledPickerInputRowDiv key={index}>
    <StyledPickerRowColor $color={getDonutArcIndexColor(index, theme, color)} />
    <StyledPickerTimeInputDiv $isDisabled>
      <InputDDHHMM granularity="days" setDayHHMM={noop} isDisabled dayHHMM={startTime} />
    </StyledPickerTimeInputDiv>
    {!isUndefined(setEndTime) && (
      <StyledPickerTimeInputDiv>
        <InputDDHHMM
          granularity="days"
          setDayHHMM={setEndTime}
          dayHHMM={endTime}
          error={error ? 'error' : undefined}
        />
      </StyledPickerTimeInputDiv>
    )}
  </StyledPickerInputRowDiv>
);

const calculateMsForBucket = (bucketSelection: DDHHMM): number => {
  const duration = Duration.fromObject({
    days: bucketSelection.days,
    hours: bucketSelection.hh,
    minutes: bucketSelection.mm,
  });
  return duration.as('milliseconds');
};

const calculateDDHHMMForMs = (ms: number): DDHHMM =>
  delayToDayHHMM({val: ms, unit: DelayUnitsEnum.MILLISECOND}, 'days');

const computeAreBucketsValid = (bucketEndTimes: number[]): boolean => {
  // Check that buckets 2 - 4 are greater than their start times.
  for (let i = 1; i < bucketEndTimes.length; i++) {
    if (bucketEndTimes[i] <= bucketEndTimes[i - 1]) {
      return false;
    }
  }

  // Check that the first bucket is greater than 0.
  if (bucketEndTimes[0] <= 0) {
    return false;
  }

  return true;
};

const computeShouldShowErrorStateForBucket = (endTime: DDHHMM, startTime?: DDHHMM): boolean =>
  // For the first bucket the starting time is always undefined since it is always 0, so
  // compare to that if startTime undefined.
  calculateMsForBucket(endTime) <= calculateMsForBucket(startTime ?? {});

const sanitizeBuckets = (buckets: ReadonlyArray<DDHHMM>, updatedIndex: number): ReadonlyArray<DDHHMM> => {
  // For each bucket, make sure we don't have overlapping values for a better UX.
  const fifteenMinutes = Duration.fromObject({minutes: 15}).as('millisecond');
  const hundredDaysMinusOneMinute = Duration.fromObject({days: 100}).minus({minutes: 1}).as('millisecond'); // Cap at 100 days minus one minute.

  const sanitizedBuckets = [...buckets];
  // Sanitize buckets before the updated one.
  for (let i = updatedIndex; i > 0; i--) {
    const currentBucketMs = calculateMsForBucket(sanitizedBuckets[i]);
    const previousBucketMs = calculateMsForBucket(sanitizedBuckets[i - 1]);
    if (previousBucketMs >= currentBucketMs) {
      sanitizedBuckets[i - 1] = calculateDDHHMMForMs(Math.max(currentBucketMs - fifteenMinutes, 0));
    }
  }
  // Sanitize buckets after the updated one.
  for (let i = updatedIndex; i < sanitizedBuckets.length - 1; i++) {
    const currentBucketMs = calculateMsForBucket(sanitizedBuckets[i]);
    const nextBucketMs = calculateMsForBucket(sanitizedBuckets[i + 1]);
    if (currentBucketMs >= nextBucketMs) {
      sanitizedBuckets[i + 1] = calculateDDHHMMForMs(
        Math.min(currentBucketMs + fifteenMinutes, hundredDaysMinusOneMinute),
      );
    }
  }
  return sanitizedBuckets;
};
